import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createChart,
  ColorType,
  CrosshairMode,
  CandlestickSeries,
  AreaSeries,
  HistogramSeries,
  LineStyle,
} from 'lightweight-charts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faSync,
  faExpand,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';
import { useTheme } from '../contexts/ThemeContext';
import { readDeskColors, withAlpha } from '../utils/deskColors';

const DURATION_PRESETS = [
  { value: '1D', label: '1D', timeframe: '5Min', limit: 120 },
  { value: '5D', label: '5D', timeframe: '15Min', limit: 200 },
  { value: '1M', label: '1M', timeframe: '1Hour', limit: 250 },
  { value: '3M', label: '3M', timeframe: '1Day', limit: 100 },
  { value: '6M', label: '6M', timeframe: '1Day', limit: 180 },
  { value: '1Y', label: '1Y', timeframe: '1Day', limit: 280 },
  { value: '5Y', label: '5Y', timeframe: '1Week', limit: 280 },
];

function isIntradayTf(tf) {
  return String(tf).includes('Min') || String(tf).includes('Hour');
}

function etDateParts(date) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
      .formatToParts(date)
      .filter((p) => p.type !== 'literal')
      .map((p) => [p.type, p.value])
  );
  return parts;
}

function formatPrice(n, digits = 2) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatVolume(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(Math.round(v));
}

function formatPct(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

function formatBars(rawBars, timeframe) {
  const intraday = isIntradayTf(timeframe);
  return (rawBars || [])
    .map((bar) => {
      try {
        let utcDate;
        if (bar.timestamp !== undefined) utcDate = new Date(bar.timestamp);
        else if (bar.t) utcDate = new Date(bar.t);
        else return null;
        if (Number.isNaN(utcDate.getTime())) return null;

        let timeValue;
        if (intraday) timeValue = Math.floor(utcDate.getTime() / 1000);
        else {
          const p = etDateParts(utcDate);
          timeValue = `${p.year}-${p.month}-${p.day}`;
        }

        const open = parseFloat(bar.o ?? bar.open);
        const high = parseFloat(bar.h ?? bar.high);
        const low = parseFloat(bar.l ?? bar.low);
        const close = parseFloat(bar.c ?? bar.close);
        const volume = parseFloat(bar.v ?? bar.volume ?? 0);
        if (![open, high, low, close].every(Number.isFinite)) return null;

        return { time: timeValue, open, high, low, close, volume };
      } catch {
        return null;
      }
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (typeof a.time === 'string' && typeof b.time === 'string') return a.time.localeCompare(b.time);
      return a.time - b.time;
    })
    .filter((bar, i, arr) => i === 0 || bar.time !== arr[i - 1].time);
}

function barTimeToDate(time) {
  if (typeof time === 'number') return new Date(time * 1000);
  return new Date(`${time}T16:00:00Z`);
}

function oldestBarEndIso(oldestBar) {
  if (!oldestBar) return null;
  const d = barTimeToDate(oldestBar.time);
  d.setUTCSeconds(d.getUTCSeconds() - 1);
  return d.toISOString();
}

function historyStartIso(endIso, timeframe) {
  const end = new Date(endIso);
  const start = new Date(end);
  if (timeframe === '1Min' || timeframe === '5Min') start.setUTCDate(start.getUTCDate() - 10);
  else if (timeframe === '15Min') start.setUTCDate(start.getUTCDate() - 30);
  else if (timeframe === '1Hour') start.setUTCMonth(start.getUTCMonth() - 4);
  else if (timeframe === '1Week') start.setUTCFullYear(start.getUTCFullYear() - 8);
  else start.setUTCFullYear(start.getUTCFullYear() - 3);
  return start.toISOString();
}

/**
 * Desk-style candlestick chart for asset detail (lightweight-charts).
 */
const HistoricalBarsChart = ({ symbol, height = 460 }) => {
  const { theme } = useTheme();
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const priceSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const barsRef = useRef([]);
  const loadingMoreRef = useRef(false);
  const hasMoreHistoryRef = useRef(true);
  const loadOlderBarsRef = useRef(null);

  const [duration, setDuration] = useState('3M');
  const [chartType, setChartType] = useState('candle');
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hud, setHud] = useState(null);

  const preset = useMemo(
    () => DURATION_PRESETS.find((d) => d.value === duration) || DURATION_PRESETS[3],
    [duration]
  );
  const timeframe = preset.timeframe;

  const lastBar = bars[bars.length - 1] || null;
  const firstBar = bars[0] || null;
  const sessionChange = useMemo(() => {
    if (!lastBar || !firstBar) return null;
    return ((lastBar.close - firstBar.close) / firstBar.close) * 100;
  }, [lastBar, firstBar]);

  const applySeriesData = useCallback(
    (formatted) => {
      if (!priceSeriesRef.current || !volumeSeriesRef.current) return;
      if (chartType === 'candle') {
        priceSeriesRef.current.setData(
          formatted.map((b) => ({
            time: b.time,
            open: b.open,
            high: b.high,
            low: b.low,
            close: b.close,
          }))
        );
      } else {
        priceSeriesRef.current.setData(
          formatted.map((b) => ({ time: b.time, value: b.close }))
        );
      }
      volumeSeriesRef.current.setData(
        formatted.map((b) => ({
          time: b.time,
          value: b.volume,
          color: b.close >= b.open ? 'rgba(61,220,151,0.35)' : 'rgba(240,113,103,0.35)',
        }))
      );
    },
    [chartType]
  );

  const createPriceSeries = useCallback(
    (chart) => {
      if (priceSeriesRef.current) {
        try {
          chart.removeSeries(priceSeriesRef.current);
        } catch {
          /* ignore */
        }
        priceSeriesRef.current = null;
      }
      const colors = readDeskColors();
      if (chartType === 'candle') {
        priceSeriesRef.current = chart.addSeries(CandlestickSeries, {
          upColor: colors.candleUp,
          downColor: colors.candleDown,
          borderVisible: false,
          wickUpColor: colors.candleUp,
          wickDownColor: colors.candleDown,
        });
      } else {
        priceSeriesRef.current = chart.addSeries(AreaSeries, {
          lineColor: colors.action,
          topColor: withAlpha(colors.action, 0.35),
          bottomColor: withAlpha(colors.action, 0.02),
          lineWidth: 2,
        });
      }
    },
    [chartType, theme]
  );

  useEffect(() => {
    if (!chartContainerRef.current) return undefined;

    const colors = readDeskColors();
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: colors.bg },
        textColor: colors.muted,
        fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
        fontSize: 11,
      },
      grid: {
        vertLines: { color: colors.grid },
        horzLines: { color: colors.grid },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: withAlpha(colors.action, 0.45),
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: colors.hover,
        },
        horzLine: {
          color: withAlpha(colors.action, 0.45),
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: colors.hover,
        },
      },
      rightPriceScale: {
        borderColor: 'rgba(255,255,255,0.08)',
        scaleMargins: { top: 0.08, bottom: 0.22 },
      },
      timeScale: {
        borderColor: 'rgba(255,255,255,0.08)',
        rightOffset: 6,
        barSpacing: 7,
        minBarSpacing: 2,
        timeVisible: true,
        secondsVisible: false,
      },
      width: chartContainerRef.current.clientWidth,
      height,
    });

    createPriceSeries(chart);

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
      lastValueVisible: false,
      priceLineVisible: false,
    });
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.82, bottom: 0 },
    });

    chartRef.current = chart;
    volumeSeriesRef.current = volumeSeries;

    chart.subscribeCrosshairMove((param) => {
      if (!param?.time || !param.seriesData?.size) {
        const latest = barsRef.current[barsRef.current.length - 1];
        if (latest) {
          setHud({
            open: latest.open,
            high: latest.high,
            low: latest.low,
            close: latest.close,
            volume: latest.volume,
            live: true,
          });
        }
        return;
      }
      const candle = param.seriesData.get(priceSeriesRef.current);
      if (!candle) return;
      const matched = barsRef.current.find((b) => b.time === param.time);
      if (chartType === 'candle' && candle.open != null) {
        setHud({
          open: candle.open,
          high: candle.high,
          low: candle.low,
          close: candle.close,
          volume: matched?.volume,
          live: false,
        });
      } else if (candle.value != null) {
        setHud({
          open: matched?.open,
          high: matched?.high,
          low: matched?.low,
          close: candle.value,
          volume: matched?.volume,
          live: false,
        });
      }
    });

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry || !chartRef.current) return;
      chartRef.current.applyOptions({ width: entry.contentRect.width, height });
    });
    ro.observe(chartContainerRef.current);

    if (barsRef.current.length) {
      applySeriesData(barsRef.current);
      chart.timeScale().fitContent();
    }

    const onVisibleRange = (range) => {
      if (!range || loadingMoreRef.current || !hasMoreHistoryRef.current) return;
      if (range.from > 25) return;
      if (typeof loadOlderBarsRef.current === 'function') loadOlderBarsRef.current();
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(onVisibleRange);

    return () => {
      try {
        chart.timeScale().unsubscribeVisibleLogicalRangeChange(onVisibleRange);
      } catch {
        /* ignore */
      }
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      priceSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, [chartType, height, createPriceSeries, applySeriesData, theme]);

  const loadBars = useCallback(async () => {
    if (!symbol) return;
    try {
      setLoading(true);
      setError(null);
      hasMoreHistoryRef.current = true;
      const response = await marketDataApi.getBars(symbol, {
        timeframe,
        limit: preset.limit,
      });
      if (!response.success || !response.data) {
        setError('No chart data returned');
        return;
      }
      const formatted = formatBars(response.data, timeframe);
      if (!formatted.length) {
        setError('No valid bars for this range');
        setBars([]);
        barsRef.current = [];
        return;
      }
      setBars(formatted);
      barsRef.current = formatted;
      const latest = formatted[formatted.length - 1];
      setHud({
        open: latest.open,
        high: latest.high,
        low: latest.low,
        close: latest.close,
        volume: latest.volume,
        live: true,
      });
      if (chartRef.current && priceSeriesRef.current) {
        applySeriesData(formatted);
        chartRef.current.timeScale().fitContent();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load chart');
    } finally {
      setLoading(false);
    }
  }, [symbol, timeframe, preset.limit, applySeriesData]);

  const loadOlderBars = useCallback(async () => {
    if (!symbol || loadingMoreRef.current || !hasMoreHistoryRef.current) return;
    const oldest = barsRef.current[0];
    if (!oldest) return;
    const endIso = oldestBarEndIso(oldest);
    if (!endIso) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const logical = chartRef.current?.timeScale().getVisibleLogicalRange();
      const response = await marketDataApi.getBars(symbol, {
        timeframe,
        limit: 300,
        start: historyStartIso(endIso, timeframe),
        end: endIso,
      });
      const raw = response?.data;
      const older = formatBars(Array.isArray(raw) ? raw : [], timeframe).filter(
        (b) => b.time < oldest.time
      );
      if (!older.length) {
        hasMoreHistoryRef.current = false;
        return;
      }
      if (older.length < 50) hasMoreHistoryRef.current = false;

      const merged = [...older, ...barsRef.current].filter(
        (b, i, arr) => i === 0 || b.time !== arr[i - 1].time
      );
      barsRef.current = merged;
      setBars(merged);
      applySeriesData(merged);
      if (logical && chartRef.current) {
        chartRef.current.timeScale().setVisibleLogicalRange({
          from: logical.from + older.length,
          to: logical.to + older.length,
        });
      }
    } catch (err) {
      console.warn('Failed to load older bars:', err?.message || err);
      hasMoreHistoryRef.current = false;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [symbol, timeframe, applySeriesData]);

  useEffect(() => {
    loadOlderBarsRef.current = loadOlderBars;
  }, [loadOlderBars]);

  useEffect(() => {
    loadBars();
  }, [loadBars]);

  const display = hud || lastBar;
  const changeTone =
    sessionChange == null
      ? 'text-[var(--lt-muted)]'
      : sessionChange >= 0
        ? 'text-[var(--lt-bull)]'
        : 'text-[var(--lt-bear)]';

  return (
    <div className="rounded-xl border border-[var(--lt-border)] bg-[var(--lt-surface)] overflow-hidden">
      <div className="px-3 py-2.5 border-b border-[var(--lt-border)] flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <div className="font-mono text-sm font-semibold text-[var(--lt-text)] tracking-wide">
            {symbol}
          </div>
          {display && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] font-mono tabular-nums text-[var(--lt-muted)]">
              <span>
                O <span className="text-[var(--lt-text)]">{formatPrice(display.open)}</span>
              </span>
              <span>
                H <span className="text-[var(--lt-bull)]">{formatPrice(display.high)}</span>
              </span>
              <span>
                L <span className="text-[var(--lt-bear)]">{formatPrice(display.low)}</span>
              </span>
              <span>
                C <span className="text-[var(--lt-text)]">{formatPrice(display.close)}</span>
              </span>
              <span>
                V <span className="text-[var(--lt-text)]">{formatVolume(display.volume)}</span>
              </span>
              {sessionChange != null && (
                <span className={changeTone}>{formatPct(sessionChange)}</span>
              )}
              {hud?.live && (
                <span className="text-[10px] uppercase tracking-wide text-[var(--lt-faint)]">last</span>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <div className="inline-flex rounded-md border border-[var(--lt-border)] overflow-hidden">
            {DURATION_PRESETS.map((d) => (
              <button
                key={d.value}
                type="button"
                onClick={() => setDuration(d.value)}
                className={`px-2 py-1 text-[11px] font-medium ${
                  duration === d.value
                    ? 'bg-[var(--lt-action-dim)] text-[var(--lt-action)]'
                    : 'text-[var(--lt-muted)] hover:bg-[var(--lt-hover)]'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-md border border-[var(--lt-border)] overflow-hidden">
            <button
              type="button"
              onClick={() => setChartType('candle')}
              className={`px-2 py-1 text-[11px] ${
                chartType === 'candle'
                  ? 'bg-[var(--lt-action-dim)] text-[var(--lt-action)]'
                  : 'text-[var(--lt-muted)] hover:bg-[var(--lt-hover)]'
              }`}
            >
              Candle
            </button>
            <button
              type="button"
              onClick={() => setChartType('area')}
              className={`px-2 py-1 text-[11px] border-l border-[var(--lt-border)] ${
                chartType === 'area'
                  ? 'bg-[var(--lt-action-dim)] text-[var(--lt-action)]'
                  : 'text-[var(--lt-muted)] hover:bg-[var(--lt-hover)]'
              }`}
            >
              Area
            </button>
          </div>
          <button
            type="button"
            onClick={loadBars}
            disabled={loading}
            className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[var(--lt-border)] text-[var(--lt-muted)] hover:bg-[var(--lt-hover)] disabled:opacity-50"
            title="Refresh"
          >
            <FontAwesomeIcon icon={loading ? faSpinner : faSync} className={`text-[10px] ${loading ? 'animate-spin' : ''}`} />
          </button>
          <Link
            to={`/charting?symbol=${encodeURIComponent(symbol)}`}
            className="h-7 px-2 inline-flex items-center gap-1 rounded-md border border-[var(--lt-border)] text-[11px] text-[var(--lt-muted)] hover:bg-[var(--lt-hover)] hover:text-[var(--lt-action)]"
            title="Open in Advanced Charting"
          >
            <FontAwesomeIcon icon={faExpand} className="text-[10px]" />
            Full
            <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[8px]" />
          </Link>
        </div>
      </div>

      <div className="relative bg-[var(--lt-bg)]">
        {loading && bars.length === 0 && (
          <div className="absolute inset-0 z-10 flex items-center justify-center text-[var(--lt-muted)]">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
            Loading {symbol}…
          </div>
        )}
        {error && (
          <div className="absolute inset-x-0 top-0 z-10 px-3 py-2 text-xs text-[var(--lt-bear)] bg-[var(--lt-bear-dim)] border-b border-[var(--lt-border)]">
            {error}
          </div>
        )}
        {loadingMore && (
          <div className="absolute left-3 bottom-3 z-10 px-2 py-1 rounded border border-[var(--lt-border)] bg-[var(--lt-surface)]/90 text-[11px] text-[var(--lt-muted)]">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1.5" />
            Loading older bars…
          </div>
        )}
        <div ref={chartContainerRef} className="w-full" style={{ height }} />
        <div className="pointer-events-none absolute right-3 top-3 text-[10px] font-mono text-[var(--lt-faint)] opacity-70">
          {timeframe}
        </div>
      </div>
    </div>
  );
};

export default HistoricalBarsChart;
